import * as THREE from 'three';
import type { EquipmentStatus } from '../components/canvas/amhs/types';

export type CabinetTexture = {
  texture: THREE.CanvasTexture;
  aspect: number;
};

const cache = new Map<string, CabinetTexture>();

function makeTexture(key: string, w: number, h: number, draw: (ctx: CanvasRenderingContext2D) => void): CabinetTexture {
  const cached = cache.get(key);
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx) draw(ctx);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  const result = { texture, aspect: w / h };
  cache.set(key, result);
  return result;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/** Touchscreen HMI matching the reference control panel. */
export function getEquipmentHmiTexture(): CabinetTexture {
  return makeTexture('hmi', 640, 480, (ctx) => {
    const w = 640;
    const h = 480;
    ctx.fillStyle = '#1A2838';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#24364A';
    ctx.fillRect(0, 0, w, 46);
    ctx.fillStyle = '#7EE0D6';
    ctx.font = '700 20px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('PROCESS  ·  LOAD PORT', 16, 24);
    ctx.fillStyle = '#9BB0C2';
    ctx.font = '600 14px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('READY', w - 16, 24);

    const cols = ['STEP', 'RECIPE', 'SEC', 'STAT'];
    const rows = [
      ['01', 'ALIGN', '12', 'OK'],
      ['02', 'LOAD', '08', 'RUN'],
      ['03', 'PURGE', '21', 'OK'],
      ['04', 'XFER', '04', 'WAIT'],
      ['05', 'UNLOAD', '00', '—'],
    ];
    ctx.fillStyle = '#152230';
    ctx.fillRect(16, 62, 400, 300);
    ctx.fillStyle = '#2C455C';
    ctx.fillRect(16, 62, 400, 32);
    ctx.fillStyle = '#D5E4EE';
    ctx.font = '700 13px ui-sans-serif, system-ui, sans-serif';
    cols.forEach((c, i) => ctx.fillText(c, 36 + i * 96, 78));
    rows.forEach((row, r) => {
      ctx.fillStyle = r === 1 ? '#1C3A40' : r % 2 ? '#182838' : '#152230';
      ctx.fillRect(16, 94 + r * 52, 400, 52);
      ctx.fillStyle = r === 1 ? '#4FE0C8' : '#C5D4E0';
      ctx.font = '600 15px ui-sans-serif, system-ui, sans-serif';
      row.forEach((cell, i) => ctx.fillText(cell, 36 + i * 96, 120 + r * 52));
    });

    const buttons = ['START', 'HOLD', 'ABORT', 'HOME'];
    buttons.forEach((label, i) => {
      const y = 62 + i * 76;
      roundRect(ctx, 432, y, 192, 64, 8);
      ctx.fillStyle = i === 0 ? '#1E5A58' : '#24364A';
      ctx.fill();
      ctx.strokeStyle = i === 0 ? '#3ED0C4' : '#3A5166';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = i === 0 ? '#7EEDE0' : '#D5E4EE';
      ctx.font = '700 18px ui-sans-serif, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(label, 528, y + 34);
    });

    ctx.fillStyle = '#0E1822';
    ctx.fillRect(16, 380, 608, 84);
    for (let i = 0; i < 18; i++) {
      const barH = 18 + ((i * 17) % 46);
      ctx.fillStyle = i < 8 ? '#2FE6D4' : '#3A5A6E';
      ctx.fillRect(28 + i * 32, 446 - barH, 20, barH);
    }
  });
}

export function getStatusReadoutTexture(status: EquipmentStatus): CabinetTexture {
  const word =
    status === 'RUNNING' ? 'BUSY' : status === 'ERROR' || status === 'WARNING' ? 'ERROR' : status === 'OFFLINE' ? 'OFF' : 'READY';
  const color =
    word === 'BUSY' ? '#F0D35C' : word === 'ERROR' ? '#E24B4B' : word === 'OFF' ? '#8A93A0' : '#3DCC5A';
  return makeTexture(`status:${word}`, 320, 160, (ctx) => {
    ctx.fillStyle = '#070808';
    ctx.fillRect(0, 0, 320, 160);
    ctx.fillStyle = '#F2F2F2';
    ctx.font = '700 28px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('STATUS', 160, 42);
    ctx.fillStyle = color;
    ctx.font = '700 42px ui-sans-serif, system-ui, sans-serif';
    ctx.fillText(word, 160, 92);
    ctx.fillRect(36, 126, 248, 10);
  });
}

export function getLedLegendTexture(): CabinetTexture {
  return makeTexture('led-legend', 700, 80, (ctx) => {
    ctx.clearRect(0, 0, 700, 80);
    const labels = ['POWER', 'READY', 'ERROR', 'DOOR', 'VAC', 'LOAD', 'UNLOAD'];
    ctx.fillStyle = '#5A5348';
    ctx.font = '700 16px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    labels.forEach((label, i) => ctx.fillText(label, 50 + i * 100, 40));
  });
}

export function getWindowInteriorTexture(): CabinetTexture {
  return makeTexture('window', 256, 512, (ctx) => {
    const g = ctx.createLinearGradient(0, 0, 256, 512);
    g.addColorStop(0, '#0A1C2E');
    g.addColorStop(0.45, '#123A58');
    g.addColorStop(1, '#0C2438');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 512);
    ctx.fillStyle = '#1A5A78';
    ctx.fillRect(28, 40, 80, 430);
    ctx.fillRect(148, 70, 70, 380);
    ctx.fillStyle = '#2FE6D4';
    ctx.globalAlpha = 0.35;
    ctx.fillRect(40, 80, 18, 360);
    ctx.fillRect(168, 110, 14, 300);
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#7EE0D6';
    ctx.globalAlpha = 0.45;
    for (let i = 0; i < 8; i++) ctx.fillRect(52, 90 + i * 46, 36, 10);
    ctx.globalAlpha = 1;
  });
}

export function getLampLabelTexture(text: string): CabinetTexture {
  return makeTexture(`lamp:${text}`, 128, 40, (ctx) => {
    ctx.clearRect(0, 0, 128, 40);
    ctx.fillStyle = '#4A433A';
    ctx.font = '700 22px ui-sans-serif, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 64, 22);
  });
}

/** Fine horizontal grain for satin load-port metallic blue panels. */
export function getBrushedAluminumMaps(): {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
} {
  const cached = cache.get('alu-blue-color') as CabinetTexture | undefined;
  const roughCached = cache.get('alu-blue-rough') as CabinetTexture | undefined;
  if (cached && roughCached) {
    return { map: cached.texture, roughnessMap: roughCached.texture };
  }
  const color = makeTexture('alu-blue-color', 256, 256, (ctx) => {
    ctx.fillStyle = '#356890';
    ctx.fillRect(0, 0, 256, 256);
    for (let y = 0; y < 256; y++) {
      const shade = 72 + ((y * 11) % 36);
      ctx.fillStyle = `rgb(${shade},${shade + 28},${shade + 52})`;
      ctx.globalAlpha = 0.22;
      ctx.fillRect(0, y, 256, 1);
    }
    ctx.globalAlpha = 0.1;
    for (let i = 0; i < 40; i++) {
      const y = (i * 19) % 256;
      ctx.fillStyle = i % 2 ? '#5C96BE' : '#284868';
      ctx.fillRect(0, y, 256, 1);
    }
    ctx.globalAlpha = 1;
  });
  const roughness = makeTexture('alu-blue-rough', 256, 256, (ctx) => {
    ctx.fillStyle = '#6A8098';
    ctx.fillRect(0, 0, 256, 256);
    for (let y = 0; y < 256; y++) {
      const v = 110 + ((y * 13) % 45);
      ctx.fillStyle = `rgb(${v},${v + 8},${v + 16})`;
      ctx.fillRect(0, y, 256, 1);
    }
  });
  color.texture.wrapS = color.texture.wrapT = THREE.RepeatWrapping;
  roughness.texture.wrapS = roughness.texture.wrapT = THREE.RepeatWrapping;
  roughness.texture.colorSpace = THREE.NoColorSpace;
  color.texture.repeat.set(2, 2);
  roughness.texture.repeat.set(3, 3);
  return { map: color.texture, roughnessMap: roughness.texture };
}

/** Neutral silver-gray brushed aluminum maps for frames, decks, and mechanisms. */
export function getSilverBrushedAluminumMaps(): {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
} {
  const cached = cache.get('alu-silver-color') as CabinetTexture | undefined;
  const roughCached = cache.get('alu-silver-rough') as CabinetTexture | undefined;
  if (cached && roughCached) {
    return { map: cached.texture, roughnessMap: roughCached.texture };
  }
  const color = makeTexture('alu-silver-color', 256, 256, (ctx) => {
    ctx.fillStyle = '#D4DAE0';
    ctx.fillRect(0, 0, 256, 256);
    for (let y = 0; y < 256; y++) {
      const shade = 190 + ((y * 11) % 35);
      ctx.fillStyle = `rgb(${shade},${shade},${shade + 2})`;
      ctx.globalAlpha = 0.18;
      ctx.fillRect(0, y, 256, 1);
    }
    ctx.globalAlpha = 0.08;
    for (let i = 0; i < 40; i++) {
      const y = (i * 19) % 256;
      ctx.fillStyle = i % 2 ? '#E2E8F0' : '#CBD5E1';
      ctx.fillRect(0, y, 256, 1);
    }
    ctx.globalAlpha = 1;
  });
  const roughness = makeTexture('alu-silver-rough', 256, 256, (ctx) => {
    ctx.fillStyle = '#8A95A0';
    ctx.fillRect(0, 0, 256, 256);
    for (let y = 0; y < 256; y++) {
      const v = 120 + ((y * 13) % 40);
      ctx.fillStyle = `rgb(${v},${v},${v})`;
      ctx.fillRect(0, y, 256, 1);
    }
  });
  color.texture.wrapS = color.texture.wrapT = THREE.RepeatWrapping;
  roughness.texture.wrapS = roughness.texture.wrapT = THREE.RepeatWrapping;
  roughness.texture.colorSpace = THREE.NoColorSpace;
  color.texture.repeat.set(2, 2);
  roughness.texture.repeat.set(3, 3);
  return { map: color.texture, roughnessMap: roughness.texture };
}

